import { Spinner } from './Spinner';

interface RefetchIndicatorProps {
    /** Position of the indicator. Default: 'top-right' */
    position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
    /** Optional text to show. Default: none (spinner only) */
    text?: string;
    /** Whether indicator is visible */
    show: boolean;
}

const positionClasses = {
    'top-right': 'top-2 right-2',
    'top-left': 'top-2 left-2',
    'bottom-right': 'bottom-2 right-2',
    'bottom-left': 'bottom-2 left-2',
};

/**
 * Unobtrusive refetch indicator for background data updates.
 * Use when cached data exists and is being refreshed.
 *
 * Requires parent container to have `position: relative`.
 *
 * @example
 * // Basic usage
 * <div className="relative">
 *     <RefetchIndicator show={isFetching && !isLoading} />
 *     {content}
 * </div>
 *
 * @example
 * // With text
 * <RefetchIndicator show={isFetching} text="Updating..." />
 */
export function RefetchIndicator({
    position = 'top-right',
    text,
    show,
}: RefetchIndicatorProps) {
    if (!show) return null;

    return (
        <div
            className={`absolute ${positionClasses[position]} flex items-center gap-1.5 rounded-sm bg-gray-100/90 dark:bg-gray-800/90 px-2 py-1 text-xs text-gray-600 dark:text-gray-300 shadow-xs z-10`}
        >
            <Spinner size="sm" />
            {text && <span>{text}</span>}
        </div>
    );
}
